import { useTranslation } from 'react-i18next';
import { Sparkles } from 'lucide-react';
import { PivotMatrix, PivotCell, PivotKey } from '@/lib/pivot';

/**
 * Cross-tab grid for a computed PivotMatrix. Sticky left column + header row,
 * right-aligned numeric cells, mixed-currency dot, row/col/grand totals. Clicking
 * a cell with drill-through fact ids opens the facts drawer; the sparkle button
 * asks the AI to explain that cell (Part 12).
 */
export function PivotGrid({
  matrix, fmt, onCellClick, onExplain,
}: {
  matrix: PivotMatrix;
  fmt: (n: number) => string;
  onCellClick: (cell: PivotCell, row: PivotKey, col: PivotKey) => void;
  onExplain?: (cell: PivotCell, row: PivotKey, col: PivotKey) => void;
}) {
  const { t } = useTranslation();
  const { rowKeys, colKeys, cells, totals } = matrix;
  const keyLabel = (k: PivotKey) => k.values.join(' / ') || '—';

  if (rowKeys.length === 0) {
    return <div className="rounded-md border-2 border-dashed border-border bg-white p-10 text-center text-ink-600">{t('common.no_data', { defaultValue: 'No data for this selection.' })}</div>;
  }

  return (
    <div className="rounded-md border border-border bg-white overflow-x-auto">
      <table className="w-full text-sm border-collapse">
        <thead>
          <tr className="bg-ink-50/70">
            <th className="sticky left-0 z-10 bg-ink-50/70 text-left font-semibold text-ink-700 px-3 py-2 border-b border-border min-w-[180px]">
              {matrix.rowDims.join(' / ')}
            </th>
            {colKeys.map((c, ci) => (
              <th key={ci} className="text-right font-semibold text-ink-700 px-3 py-2 border-b border-border whitespace-nowrap num">{keyLabel(c)}</th>
            ))}
            <th className="text-right font-semibold text-ink-900 px-3 py-2 border-b border-l border-border whitespace-nowrap">{t('common.total', { defaultValue: 'Total' })}</th>
          </tr>
        </thead>
        <tbody>
          {rowKeys.map((r, ri) => (
            <tr key={ri} className="hover:bg-emerald-50/30">
              <td className="sticky left-0 z-10 bg-white text-left text-ink-800 px-3 py-1.5 border-b border-border/60 truncate max-w-[260px]">{keyLabel(r)}</td>
              {colKeys.map((c, ci) => {
                const cell = cells[`${ri}|${ci}`];
                if (!cell) return <td key={ci} className="text-right px-3 py-1.5 border-b border-border/60 text-ink-300 num">·</td>;
                const drillable = cell.factIds.length > 0;
                const mixed = cell.sourceCurrencies.length > 1;
                return (
                  <td key={ci} className="group text-right px-3 py-1.5 border-b border-border/60 num tabular-nums">
                    <span className="inline-flex items-center gap-1">
                      {drillable && onExplain && (
                        <button
                          type="button"
                          onClick={() => onExplain(cell, r, c)}
                          title={t('common.explain_number', { defaultValue: 'Explain this number' })}
                          className="opacity-0 group-hover:opacity-100 text-violet-400 hover:text-violet-600 transition-opacity"
                        >
                          <Sparkles className="h-3 w-3" />
                        </button>
                      )}
                      <button
                        type="button"
                        disabled={!drillable}
                        onClick={() => onCellClick(cell, r, c)}
                        className={`inline-flex items-center gap-1 ${drillable ? 'text-ink-800 hover:text-emerald-700 hover:underline cursor-pointer' : 'text-ink-700 cursor-default'}`}
                        title={mixed ? `Mixed currencies: ${cell.sourceCurrencies.join(', ')}` : cell.sourceCurrencies[0] ?? ''}
                      >
                        {mixed && <span className="h-1.5 w-1.5 rounded-full bg-amber-400" aria-hidden />}
                        {fmt(cell.amount)}
                      </button>
                    </span>
                  </td>
                );
              })}
              <td className="text-right px-3 py-1.5 border-b border-l border-border/60 num tabular-nums font-semibold text-ink-900">{fmt(totals.rowTotals[ri] ?? 0)}</td>
            </tr>
          ))}
          <tr className="bg-ink-50/70 font-semibold">
            <td className="sticky left-0 z-10 bg-ink-50/70 text-left text-ink-900 px-3 py-2 border-t border-border">Total</td>
            {colKeys.map((_, ci) => (
              <td key={ci} className="text-right px-3 py-2 border-t border-border num tabular-nums text-ink-900">{fmt(totals.colTotals[ci] ?? 0)}</td>
            ))}
            <td className="text-right px-3 py-2 border-t border-l border-border num tabular-nums text-emerald-700">{fmt(totals.grandTotal)}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
}
